import { api } from '../client';
import type { PageParams } from '@/types/api';
import type { Employee, EmploymentStatus, EmploymentType, Teacher, UUID } from '@/types/domain';

export interface EmployeePayload {
  userId: UUID;
  /** Generated when omitted. */
  employeeNumber?: string;
  joiningDate: string;
  employmentType: EmploymentType;
  designation?: string;
}

export interface EmployeeStatusPayload {
  status: EmploymentStatus;
  /** Required when employment ends: RESIGNED, TERMINATED or RETIRED. */
  leavingDate?: string;
  reason?: string;
}

export interface TeacherPayload {
  employeeId: UUID;
  specialization?: string;
  qualificationSummary?: string;
  yearsOfExperience?: number;
  maxWeeklyPeriods?: number;
}

export const employeesApi = {
  list: (params: PageParams & { status?: EmploymentStatus }) =>
    api.getPage<Employee>('/employees', { params }),
  get: (id: UUID) => api.get<Employee>(`/employees/${id}`),
  create: (payload: EmployeePayload) => api.post<Employee>('/employees', payload),
  update: (id: UUID, payload: EmployeePayload) => api.put<Employee>(`/employees/${id}`, payload),
  /**
   * Ending employment records a leaving date and keeps the row: past timetables,
   * assignments and attendance all still point at it.
   */
  changeStatus: (id: UUID, payload: EmployeeStatusPayload) =>
    api.patch<Employee>(`/employees/${id}/status`, payload),
};

export const teachersApi = {
  list: (params: PageParams) => api.getPage<Teacher>('/teachers', { params }),
  get: (id: UUID) => api.get<Teacher>(`/teachers/${id}`),
  create: (payload: TeacherPayload) => api.post<Teacher>('/teachers', payload),
  update: (id: UUID, payload: TeacherPayload) => api.put<Teacher>(`/teachers/${id}`, payload),
};
